<template>
    <card-skeleton v-if="hasOrders" data-id="orderHistoryCard" :is-loading="isLoading && !isMoreLoading">
        <v-card-item>
            <card-title :text="t('order.orderHistory')" />
        </v-card-item>
        <v-card-text>
            <order-history-cards :orders="ordersList" :language="language" />
            <div class="text-center ma-0">
                <load-more-button
                    data-id="orderHistoryLoadMoreButton"
                    :disabled="isLoadMoreDisabled"
                    :loading="isMoreLoading"
                    @click="handleLoadMore"
                    :text="orderButtonText"
                />
            </div>
        </v-card-text>
    </card-skeleton>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { debounce } from 'lodash-es';
import { IOrderHistoryPage, OrderHistoryDto } from '../types';
import { useAppConfiguration } from '@/composables';
import { DEBOUNCE_DELAY, TAKE_OPTIONS } from '@/constants';
import { useI18n } from 'vue-i18n';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import CardTitle from '@/features/common/components/CardTitle.vue';
import OrderHistoryCards from '../components/list/OrderHistoryCardList.vue';
import { getOrders } from '../services/api';
import LoadMoreButton from '@/features/common/components/LoadMoreButton.vue';
import { mergeOrderLists, isMaxItemsReached, isPendingCardFoundInRecentOrders } from '@/features/OrderHistory/helpers';
import { getFirst, getTravelCardNumberList, isWithinDaysFromNow } from '@/helpers';
import { useOrdersStore } from '../store/cardStore';
import { storeToRefs } from 'pinia';
import { useCardStore } from '@/features/TravelCards/store/cardStore';
import { ORDER_BATCH_SIZE } from '../constants';

const props = withDefaults(defineProps<IOrderHistoryPage>(), { take: TAKE_OPTIONS.DEFAULT });

const skip = ref(0);
const total = ref(0);
const isLoading = ref(true);
const isMoreLoading = ref(true);

const expanded = ref(false);

const { t } = useI18n();
const { language, config } = useAppConfiguration();

const cardStore = useCardStore();
const { items: cardItems } = storeToRefs(cardStore);
const { setRecentPendingOrders } = cardStore;

const orderStore = useOrdersStore();
const { items: orderItems } = storeToRefs(orderStore);
const { setItems } = orderStore;

const maxItemsReached = computed(() => isMaxItemsReached(total.value, skip.value, props.take));
const hasOrders = computed(() => orderItems.value.length);
const isAllOrdersNotFetched = computed(() => total.value !== orderItems.value.length);
const currentOrderCount = computed(() => skip.value + props.take);

const checkPendingTravelCard = (): void => {
    const userCardNumbers = getTravelCardNumberList(cardItems.value);

    if (!userCardNumbers?.length) return;

    const recentOrders = orderItems.value?.filter((order: OrderHistoryDto) =>
        isWithinDaysFromNow(Number(order.createdAt), config.value.travelCardOrderedMessageDays)
    ) as OrderHistoryDto[];

    setRecentPendingOrders(isPendingCardFoundInRecentOrders(recentOrders, userCardNumbers));
};

const fetchOrders = async (takeSize: number = ORDER_BATCH_SIZE, skipSize: number = skip.value) => {
    try {
        const { orders = [], totalMatching = 0 } = (await getOrders(takeSize, skipSize, language.value)) || {};
        const mergedList = mergeOrderLists(orderItems.value || [], orders, totalMatching, skip.value, TAKE_OPTIONS.ALL);

        total.value = mergedList.totalMatching;
        setItems(mergedList.orders);
    } catch {
        setItems([]);
    } finally {
        isLoading.value = false;
        isMoreLoading.value = false;
    }
};

const debouncedFetchOrders = debounce(fetchOrders, DEBOUNCE_DELAY);

    const handleLoadMore = () => {

        if (expanded.value && maxItemsReached.value) {
            isMoreLoading.value = false;
            skip.value = 0;
            expanded.value = false;
            return;
        }

        if (maxItemsReached.value) return;
        isMoreLoading.value = true;
        isAllOrdersNotFetched.value ? debouncedFetchOrders(props.take, ORDER_BATCH_SIZE + skip.value) : (isMoreLoading.value = false);
        skip.value += props.take;

        if (skip.value > 0) expanded.value = true;
    };

const ordersList = computed(() => getFirst(orderItems.value as OrderHistoryDto[], currentOrderCount.value));

const isLoadMoreDisabled = computed(() => {
    return isMoreLoading.value || !orderItems.value?.length || (maxItemsReached.value && !expanded.value);
});

const orderButtonText = computed(() => (expanded.value && maxItemsReached.value ? 'startPage.showLess' : 'startPage.loadMore'));


watch(
    [cardItems, orderItems],
    (value, oldValue) => {
        if (value !== oldValue) {
            checkPendingTravelCard();
        }
    },
    { immediate: false }
);

onMounted(() => {
    debouncedFetchOrders();
});
</script>
